/**
 * Practice-switch route resolution.
 *
 * Historically the header practice switcher hard-navigated to `/dashboard`
 * on every switch, so an operator comparing the same table across two
 * practices had to re-navigate after each switch. This module answers the
 * question the switcher actually needs to ask:
 *
 *     "I am at <pathname> and the practice is about to become <toPracticeId>.
 *      Where should I be?"
 *
 * Three outcomes:
 *   - KEEP      the path is practice-agnostic (state comes from Redux) -> stay put
 *   - REWRITE   the path carries a `/practice/<id>/` segment -> swap the id
 *   - LAND      the path identifies an entity that belongs to the OLD practice
 *               -> fall back to that section's list page, or the dashboard
 *
 * Deliberately dependency-free (no React, no Redux, no MUI) so it unit-tests
 * in milliseconds and can be reasoned about in isolation.
 *
 * NOTE ON QUERY STRINGS: callers pass `pathname` only and must drop
 * `search`/`hash`. Query params routinely carry old-practice entity ids
 * (`?lead=`, `?templateId=`, `?highlight=`) which would 404 or, worse,
 * silently render another practice's record after the switch.
 */

export const DASHBOARD_PATH = '/dashboard/app';

const DASHBOARD_ROOT = '/dashboard';

// Section landing pages used when an entity id has to be dropped.
const SECTION = {
    tasksV2: (practiceId) => `/dashboard/practice/${practiceId}/tasks-v2`,
    tasksV2Templates: (practiceId) => `/dashboard/practice/${practiceId}/tasks-v2/templates`,
    tasksV1: '/dashboard/tasks',
    tasksV2Alias: '/dashboard/tasks-v2',
    whatsapp: (practiceId) => `/dashboard/practice/${practiceId}/whatsapp`,
    instagram: (practiceId) => `/dashboard/practice/${practiceId}/instagram`,
    instagramDm: (practiceId) => `/dashboard/practice/${practiceId}/instagram-dm`,
    practiceDetails: '/dashboard/practice/details',
    practiceUsers: '/dashboard/practice/practice-user',
    callScripts: '/dashboard/practice/call-scripts',
    consentBuilder: '/dashboard/practice/consent',
    practiceChatbot: '/dashboard/practice/practice-chatbot',
    campaigns: '/dashboard/campaigns-manager/campaigns',
    audienceManager: '/dashboard/campaigns-manager/audience_manager',
    websiteAnnotation: '/dashboard/website-annotation',
};

/** Strip a trailing slash (but never turn a path into ''). */
const trimSlash = (p) => (p.length > 1 && p.endsWith('/') ? p.replace(/\/+$/, '') : p);

const segmentsOf = (pathname) => trimSlash(pathname).split('/').filter(Boolean);

/**
 * Sub-resolver for `/dashboard/practice/<numeric id>/<rest>` URLs.
 * `rest` is the array of segments AFTER the practice id.
 */
function resolvePracticeScoped(rest, toPracticeId) {
    const [head, ...tail] = rest;

    switch (head) {
        // ---- Lead + archive detail: the id belongs to the old practice and
        // there is no per-practice "lead list" equivalent (leads are spread
        // across half a dozen status tables), so the dashboard is the honest
        // landing spot.
        case 'lead-detail':
        case 'lead-detail-v2':
        case 'lead-detail-v3':
        case 'lead-detail-v4':
        case 'archived':
            return DASHBOARD_PATH;

        // ---- Tasks V2 subtree ------------------------------------------------
        case 'tasks-v2': {
            if (tail.length === 0) return SECTION.tasksV2(toPracticeId);
            // Super-admin template surfaces collapse to the template list.
            if (tail[0] === 'templates') return SECTION.tasksV2Templates(toPracticeId);
            // Internal demo/actions playgrounds are practice-scoped but idless.
            if (tail[0] === '_demo' || tail[0] === '_actions') {
                return `${SECTION.tasksV2(toPracticeId)}/${tail[0]}`;
            }
            // Anything else is a task uuid -> drop to the task board.
            return SECTION.tasksV2(toPracticeId);
        }

        // ---- Messaging inboxes: thread ids are practice-owned, the inbox is
        // the nav destination, so drop the thread and keep the inbox.
        case 'whatsapp':
            return SECTION.whatsapp(toPracticeId);
        case 'instagram':
            return SECTION.instagram(toPracticeId);
        case 'instagram-dm':
            return SECTION.instagramDm(toPracticeId);

        // ---- `/dashboard/practice/<id>/...` also collides with a handful of
        // settings-entity editors whose `<id>` is NOT a practice id.
        case 'edit':          // practice user editor
            return SECTION.practiceUsers;
        case 'edit-script':
            return SECTION.callScripts;
        case 'edit-consent':
            return SECTION.consentBuilder;

        default:
            return DASHBOARD_PATH;
    }
}

/**
 * Sub-resolver for the `/dashboard/practice/<non-numeric>` settings namespace.
 * `rest` is the array of segments after `practice`.
 */
function resolvePracticeSettings(rest) {
    const [head, ...tail] = rest;

    switch (head) {
        // `/practice/details/:id` pins the page to a specific practice and
        // syncs that id BACK into Redux on mount — leaving the id in place
        // would immediately undo the switch. Drop to the idless form, which
        // follows the Redux practice.
        case 'details':
            return tail.length ? SECTION.practiceDetails : null;

        // Creation forms: the draft was started for the previous practice.
        // Land on the matching list rather than silently re-targeting it.
        case 'new':
            return SECTION.practiceUsers;
        case 'create-script':
            return SECTION.callScripts;
        case 'create-consent':
            return SECTION.consentBuilder;
        case 'create-chatbot':
            return SECTION.practiceChatbot;
        case 'edit-chatbot':
            return SECTION.practiceChatbot;

        // Practitioner rota for one practitioner of the old practice.
        case 'practitioner-schedule':
            return SECTION.practiceUsers;

        default:
            return null; // keep — plain practice-scoped settings page
    }
}

function resolveCampaignsManager(rest) {
    const [head, ...tail] = rest;

    if (head === 'builder') return SECTION.campaigns;                 // /builder and /builder/:id
    if (head === 'campaigns' && tail.length) return SECTION.campaigns; // /campaigns/:id/report
    if (head === 'create_audience') return SECTION.audienceManager;
    if (tail[0] === 'edit_audience') return SECTION.audienceManager;   // /:id/edit_audience
    return null;
}

/**
 * Resolve where the app should navigate when the active practice changes.
 *
 * @param {object}        args
 * @param {string}        args.pathname      current location.pathname (no search/hash)
 * @param {string|number} args.toPracticeId  the practice being switched TO
 * @returns {string} absolute pathname to navigate to
 */
export function resolvePracticeSwitchTarget({ pathname, toPracticeId } = {}) {
    if (!pathname || typeof pathname !== 'string') return DASHBOARD_PATH;

    const segments = segmentsOf(pathname);

    // Anything outside the dashboard shell (auth, public forms, 404) has no
    // meaningful practice-scoped equivalent.
    if (segments[0] !== 'dashboard') return DASHBOARD_PATH;

    // Bare `/dashboard` already redirects to the dashboard page.
    if (segments.length === 1) return DASHBOARD_PATH;

    const [, second, ...rest] = segments;

    // Super admin is a global, user-scoped surface — a practice switch must
    // not eject the operator out of it.
    if (second === 'super-admin') return trimSlash(pathname);

    // A dead-end the user was bounced to under the PREVIOUS practice; the new
    // practice may well allow it, so send them somewhere useful instead.
    if (second === 'permission-denied') return DASHBOARD_PATH;

    // OAuth return URLs are transient and single-practice by nature.
    if (['stripe', 'facebook', 'google', 'tiktok', 'google-ads', 'google-analytics'].includes(second)) {
        return DASHBOARD_PATH;
    }

    if (second === 'practice') {
        // `/dashboard/practice/<numeric>/...` — practice-scoped feature pages
        // (plus a few settings editors whose id is an entity, not a practice).
        if (/^\d+$/.test(rest[0] || '')) {
            return resolvePracticeScoped(rest.slice(1), toPracticeId);
        }
        return resolvePracticeSettings(rest) || trimSlash(pathname);
    }

    if (second === 'campaigns-manager') {
        return resolveCampaignsManager(rest) || trimSlash(pathname);
    }

    if (second === 'website-annotation' && rest.length) {
        return SECTION.websiteAnnotation;
    }

    // Legacy `/dashboard/user/*` URLs are pure redirect shims — let them
    // resolve from the dashboard rather than bouncing through a redirect
    // that may itself carry an old-practice id.
    if (second === 'user') return DASHBOARD_PATH;

    // Everything else (all lead tables, reporting tabs, emails, forms,
    // calendar, storage, settings pages …) reads its practice from Redux.
    return trimSlash(pathname);
}

/**
 * Super-admin pages are global, user-scoped surfaces whose query string holds
 * their own filter state (`?practice=`, `?useCase=`, `?templateId=`) rather
 * than anything owned by the active practice. Switching practice there is a
 * context change for the REST of the app, so leave those URLs completely
 * alone — including the search string every other route deliberately drops.
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export function shouldPreserveQuery(pathname) {
    if (!pathname || typeof pathname !== 'string') return false;
    return segmentsOf(pathname)[1] === 'super-admin';
}

// ---------------------------------------------------------------------------
// Post-switch reconciliation
// ---------------------------------------------------------------------------
// Feature availability and the Tasks V1/V2 rollout are per-practice and are
// only known once `fetchMyPermissions(newPracticeId)` resolves — i.e. AFTER
// the navigation above has already happened. `getRouteRequirement` describes
// what the landed-on route needs so the caller can bounce once the answer
// arrives. Keeping this table next to the resolver means a new guarded route
// is added in one place, not two.

/** Routes whose `<FeatureGuard>` would redirect to /permission-denied. */
const FEATURE_GATED_ROUTES = [
    { test: (p) => p === '/dashboard/analytics/online-booking', feature: 'pms-scheduling' },
    { test: (p) => p === '/dashboard/tables/dentally-patients', feature: 'dentally-integration' },
    { test: (p) => p === '/dashboard/tables/consultation-attended-v2', feature: 'ca-pipeline-v2' },
    { test: (p) => p === '/dashboard/tables/aligner-approve-table', feature: 'aligner-approve' },
    { test: (p) => p === '/dashboard/analytics-reports/geo-locator', feature: 'geo-locator' },
    { test: (p) => p === '/dashboard/practice/practice-chatbot', feature: 'chatbot' },
];

/**
 * @param {string} pathname
 * @returns {{kind:'feature', feature:string} | {kind:'tasks', version:'v1'|'v2'} | null}
 */
export function getRouteRequirement(pathname) {
    if (!pathname || typeof pathname !== 'string') return null;
    const clean = trimSlash(pathname);

    const gated = FEATURE_GATED_ROUTES.find((r) => r.test(clean));
    if (gated) return { kind: 'feature', feature: gated.feature };

    if (clean === SECTION.tasksV1) return { kind: 'tasks', version: 'v1' };
    if (clean === SECTION.tasksV2Alias) return { kind: 'tasks', version: 'v2' };
    if (/^\/dashboard\/practice\/\d+\/tasks-v2(\/|$)/.test(clean)) return { kind: 'tasks', version: 'v2' };

    return null;
}

/**
 * Where to bounce when the requirement is not met under the new practice.
 *
 * Tasks is special: both versions are the SAME sidebar entry, so a V2->V1
 * practice keeps the operator on Tasks rather than dumping them on the
 * dashboard. Everything else falls back to the dashboard.
 *
 * @param {ReturnType<typeof getRouteRequirement>} requirement
 * @param {{hasTasksV2:boolean, hasFeature:(f:string)=>boolean}} capabilities
 * @returns {string|null} path to redirect to, or null when the route is fine
 */
export function resolveRequirementFallback(requirement, capabilities) {
    if (!requirement) return null;
    const { hasTasksV2, hasFeature } = capabilities || {};

    if (requirement.kind === 'feature') {
        if (typeof hasFeature !== 'function') return null;
        return hasFeature(requirement.feature) ? null : DASHBOARD_PATH;
    }

    if (requirement.kind === 'tasks') {
        if (typeof hasTasksV2 !== 'boolean') return null;
        if (requirement.version === 'v2' && !hasTasksV2) return SECTION.tasksV1;
        if (requirement.version === 'v1' && hasTasksV2) return SECTION.tasksV2Alias;
        return null;
    }

    return null;
}

/**
 * Should a pending `practiceIdToChange` actually run the practice-switch routine?
 *
 * The All Patients / Inter-Practice tables set `practiceIdToChange` to jump the
 * operator into the practice that owns the row they clicked. But
 * practiceTreatmentList seeds BOTH `practiceId` and `practiceIdToChange` from
 * the same stored id, so on a cold page load the header's effect saw a pending
 * "switch" to the practice already selected and ran the whole routine — which
 * included a second fetchMyPermissions and made every page load request
 * /permissions/my-permissions/ twice.
 *
 * A switch to the currently selected practice has nothing to do, so it is
 * skipped. Any genuine cross-practice jump still differs and still switches.
 * Compared as strings because the stored id arrives from localStorage as a
 * string while Redux usually holds a number.
 */
export function shouldApplyPendingPracticeChange(practiceIdToChange, currentPracticeId) {
    if (practiceIdToChange == null || practiceIdToChange === '') return false;
    if (currentPracticeId == null || currentPracticeId === '') return true;
    return String(practiceIdToChange) !== String(currentPracticeId);
}

export const __testables = { SECTION, DASHBOARD_ROOT };
